import { useQuery } from 'react-query';
import { 
  fetchProductImage,
  fetchFinancialSummary,
  fetchProductReportPerformance,
  fetchCollectionPrediction,
  fetchHistoricalMatrix,
  fetchProductTag,
  fetchProductImagesByIds
} from "./services/AssortmentService";
import { CACHE_TTL_MS } from './ttl.cache';

export const useAssortment = (options = {}) => {
  const { 
    shouldFetchFinancialSummry = false,
    shouldFetchProductPerformanceSummary = false,
    shouldFetchCollectionPrediction = false,
    shouldFetchHistoricalMatrix = false,
    selectedProductId = null,
    selectedTagProductId = null,
    seasonId = null,
    productIdsForPDFGeneration = null,
    pdfExportSeasonId = null
  } = options;

  const financialSummaryData = useQuery({
    queryKey: ["assortment-financial-summary"],
    queryFn: async () => {
      const data = await fetchFinancialSummary();
      return data;
    },
    enabled: shouldFetchFinancialSummry,
    staleTime: CACHE_TTL_MS.TTL_12_HOURS,
    cacheTime: CACHE_TTL_MS.TTL_12_HOURS,
    refetchOnMount: false,
  });  

  const productPerformanceSummaryData = useQuery({
    queryKey: ["assortment-product-performance-summary"],
    queryFn: async () => {
      const data = await fetchProductReportPerformance();
      return data;
    },
    enabled: shouldFetchProductPerformanceSummary,
    staleTime: CACHE_TTL_MS.TTL_12_HOURS,
    cacheTime: CACHE_TTL_MS.TTL_12_HOURS,
    refetchOnMount: false,
  }); 

  const collectionPredictionData = useQuery({
    queryKey: ["assortment-collection-prediction"],
    queryFn: async () => {
      const data = await fetchCollectionPrediction();
      return data;
    },
    enabled: shouldFetchCollectionPrediction,
    staleTime: CACHE_TTL_MS.TTL_12_HOURS,
    cacheTime: CACHE_TTL_MS.TTL_12_HOURS,
    refetchOnMount: false,
  });   

  const historicalMatrixData = useQuery({
    queryKey: ["assortment-historical-matrix"],
    queryFn: async () => {
      const data = await fetchHistoricalMatrix();
      return data;
    },
    enabled: shouldFetchHistoricalMatrix,
    staleTime: CACHE_TTL_MS.TTL_12_HOURS,
    cacheTime: CACHE_TTL_MS.TTL_12_HOURS,
    refetchOnMount: false,
  });     

  const productImageData = useQuery({
    queryKey: ["assortment", "product-image", selectedProductId],
    queryFn: () => fetchProductImage(selectedProductId),
    enabled: !!selectedProductId,
    staleTime: CACHE_TTL_MS.TTL_12_HOURS,
    cacheTime: CACHE_TTL_MS.TTL_12_HOURS,
    refetchOnMount: false,
  });

  const productTagData = useQuery({
    queryKey: ["assortment", "product-tag", selectedTagProductId, seasonId],
    queryFn: () => fetchProductTag(seasonId, selectedTagProductId),
    enabled: !!selectedTagProductId && !!seasonId,
    staleTime: CACHE_TTL_MS.TTL_12_HOURS,
    cacheTime: CACHE_TTL_MS.TTL_12_HOURS,
    refetchOnMount: false,
  }); 
  
  const productImgForPDFGeneration = useQuery({
    queryKey: ["assortment", "product-pdf", productIdsForPDFGeneration, pdfExportSeasonId],
    queryFn: () => fetchProductImagesByIds(pdfExportSeasonId, productIdsForPDFGeneration),
    enabled: !!productIdsForPDFGeneration?.length && !!pdfExportSeasonId,
    staleTime: CACHE_TTL_MS.TTL_12_HOURS,
    cacheTime: CACHE_TTL_MS.TTL_12_HOURS,
    refetchOnMount: false,
  });   

  return {
    financialSummary: {
      data: financialSummaryData.data,
      isLoading: financialSummaryData.isLoading,
    },    
    productPerformanceSummary: {
      data: productPerformanceSummaryData.data,
      isLoading: productPerformanceSummaryData.isLoading,
    },   
    collectionPrediction: {
      data: collectionPredictionData.data,
      isLoading: collectionPredictionData.isLoading,
    },    
    historicalMatrix: {
      data: historicalMatrixData.data,
      isLoading: historicalMatrixData.isLoading,
    },                 
    productImage: {
      data: productImageData.data,
      isLoading: productImageData.isLoading,
    },
    productTag: {
      data: productTagData.data,
      isLoading: productTagData.isLoading
    },
    productImgForPDFGeneration: {
      data: productImgForPDFGeneration.data,
      isLoading: productImgForPDFGeneration.isLoading
    }
  };
};